import { Box, type DrawerProps, IconButton, Typography } from "@mui/material";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { AnalysisClientItemRelationType, type Client } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useRef } from "react";
import { EditClientForm } from "../Detail/EditClientForm";
import { Drawer } from "../_Shared/Drawer";
import { FamilyMemberForm } from "./FamilyMemberForm";

type Props = {
  readonly clientId?: string;
  readonly clientRelationType?: AnalysisClientItemRelationType;
  readonly onClose: () => void;
  readonly clientGuid?: Client["guid"];
} & DrawerProps;

const getTitle = (
  t: ReturnType<typeof useResource>["t"],
  clientId?: string,
  clientGuid?: string,
  clientRelationType?: AnalysisClientItemRelationType,
): string => {
  if (clientGuid && clientRelationType === AnalysisClientItemRelationType.Client) {
    return t(Resources.AnalysisDetail.Family.MemberDrawer.EditClientTitle);
  }

  if (clientId && clientRelationType === AnalysisClientItemRelationType.RelatedClient) {
    return t(Resources.AnalysisDetail.Family.MemberDrawer.EditFamilyMemberTitle);
  }

  return t(Resources.AnalysisDetail.Family.MemberDrawer.AddFamilyMemberTitle);
};

export const FamilyMemberDrawer: React.FC<Props> = ({ clientId, clientRelationType, clientGuid, onClose, ...rest }) => {
  const { t } = useResource();

  // Freeze the form-selection props while the drawer is animating closed.
  // When the parent resets clientRelationType on close, `open` is already false
  // but the exit animation is still running, causing a flash of the wrong form.
  const stablePropsRef = useRef({ clientId, clientRelationType, clientGuid });
  if (rest.open) {
    stablePropsRef.current = { clientId, clientRelationType, clientGuid };
  }
  const { clientId: stableClientId, clientRelationType: stableRelationType, clientGuid: stableClientGuid } = stablePropsRef.current;

  return (
    <Drawer {...rest} onClose={onClose}>
      <Box display="flex" justifyContent="space-between" alignItems="center" padding={Spacing.XXL}>
        <Typography variant="h2">{getTitle(t, stableClientId, stableClientGuid, stableRelationType)}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box px={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
        {stableClientGuid && stableRelationType === AnalysisClientItemRelationType.Client && (
          <EditClientForm onClose={onClose} clientGuid={stableClientGuid} />
        )}
        {stableRelationType !== AnalysisClientItemRelationType.Client && <FamilyMemberForm clientId={stableClientId} onClose={onClose} />}
      </Box>
    </Drawer>
  );
};
